import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import DateRangePicker from '@risk-smart/themed-cloudscape-components/date-range-picker';
import { useAmplitude } from '@risksmart-app/components/src/segment/useAnalytics';
import type { FC, KeyboardEvent, MouseEvent } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { createDefaultValidator } from '@/components/form/controlled-date-range-picker/dateRangePickerUtils';

import {
  DEFAULT_INDICATOR_RESULTS_RANGE,
  INDICATOR_RESULTS_RELATIVE_OPTIONS,
  isNotFutureDate,
} from './dateRange';

interface Props {
  value: DateRangePickerProps.Value;
  onChange: (value: DateRangePickerProps.Value) => void;
}

/**
 * Date range picker for the indicator results chart (CUS-142). Wraps the themed
 * Cloudscape DateRangePicker, which commits only on Apply (so selecting a range
 * does not move the chart) and reverts pending edits when dismissed without
 * applying.
 *
 * "Reset to default" reuses the picker's built-in clear control, relabelled and
 * coalesced to the Last-6-months default — Cloudscape exposes no pending-state
 * hook, so reset applies immediately rather than staging an un-applied
 * selection.
 *
 * Analytics (CUS-146): Apply and Reset are tracked via `onChange` (one event,
 * `is_reset` + range properties — mid-dropdown selections aren't exposed by
 * Cloudscape's API, so the applied value is the tracked selection). Opening
 * the picker is detected with capture-phase handlers on a wrapping span (open
 * *intent*, mirroring ActionsButton's onOpen); gestures inside the dropdown
 * (`[role="dialog"]`) are ignored.
 */
const isFromDropdown = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[role="dialog"]') !== null;

export const ResultsDateRangePicker: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('common');
  const translations = t('dateRangeInput', { returnObjects: true });
  const amplitude = useAmplitude();
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const trackOpen = () => {
    void amplitude.track({
      event_type: 'Indicator Results Date Range Opened',
    });
  };

  // The picker renders its dropdown as a `[role="dialog"]` inside the wrapper
  // (no expandToViewport). When one is already present, this gesture on the
  // trigger is a *close*, not an open — don't emit a second "Opened" event.
  const isAlreadyOpen = () =>
    wrapperRef.current?.querySelector('[role="dialog"]') != null;

  const handleMouseDownCapture = (event: MouseEvent<HTMLSpanElement>) => {
    if (isFromDropdown(event.target) || isAlreadyOpen()) {
      return;
    }
    trackOpen();
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (
      event.key !== 'Enter' &&
      event.key !== ' ' &&
      event.key !== 'ArrowDown'
    ) {
      return;
    }
    if (isFromDropdown(event.target) || isAlreadyOpen()) {
      return;
    }
    trackOpen();
  };

  const apply = (next: DateRangePickerProps.Value | null) => {
    const isReset = next === null;
    const applied = next ?? DEFAULT_INDICATOR_RESULTS_RANGE;
    onChange(applied);
    void amplitude.track({
      event_type: 'Indicator Results Date Range Applied',
      event_properties: {
        range_type: applied.type,
        is_reset: isReset,
        ...(applied.type === 'relative'
          ? {
              relative_key: applied.key,
              amount: applied.amount,
              unit: applied.unit,
            }
          : { start_date: applied.startDate, end_date: applied.endDate }),
      },
    });
  };

  return (
    <span
      ref={wrapperRef}
      onMouseDownCapture={handleMouseDownCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <DateRangePicker
        value={value}
        onChange={(e) => apply(e.detail.value)}
        relativeOptions={INDICATOR_RESULTS_RELATIVE_OPTIONS}
        isValidRange={createDefaultValidator(t)}
        isDateEnabled={(date) => isNotFutureDate(date)}
        dateOnly
        absoluteFormat={'long-localized'}
        hideTimeOffset={true}
        i18nStrings={{
          formatRelativeRange: ({ unit, amount }) =>
            t(`relativeTimes.previous.${unit}`, { count: -amount }),
          ...translations,
          clearButtonLabel: t('indicator_results.date_range.reset_to_default'),
        }}
      />
    </span>
  );
};

export default ResultsDateRangePicker;
